/**
 * Single source of truth for data-visualisation colour and chart chrome.
 *
 * Mirrors the Sequesto product dashboard palette so the OS and the product read
 * as one system.
 *
 * These are deliberately NOT the Tailwind `accent` tokens. The accent (#10b981)
 * drives interactive chrome — buttons, pills, badges — while these drive data.
 * Keeping the two separate means a brand tweak cannot silently re-colour every
 * chart, and a new chart series cannot leak into the button colour.
 */

/**
 * Ordered categorical series colours, drawn from the sequesto.com palette: ink
 * first, then warm greys and earth tones. Always assign by index via
 * {@link seriesColor} so the same legend position means the same colour on
 * every dashboard. Indigo is absent: it is reserved for links, focus and
 * selection. Green, amber and red are absent: they are status colours, never
 * "series 4".
 */
export const CHART_SERIES = [
  '#121212', // ink        — primary series
  '#7A6A58', // umber      — secondary series
  '#A8A29A', // stone      — comparison / previous period
  '#4A5560', // slate
  '#C2AE8E', // sand
  '#5E5A55', // graphite
  '#D8D1C8', // mist
] as const;

/** Kept for callers that asked for extra contrast; the brand series already passes on white. */
export const CHART_SERIES_HIGH_CONTRAST = CHART_SERIES;

/**
 * Series beyond this count stop being distinguishable no matter the palette.
 * Group the tail into "Other" or split into small multiples rather than cycling.
 */
export const MAX_DISTINCT_SERIES = CHART_SERIES.length;

/** Meaning-bearing colours. Use these when a series *means* good/bad, not just "series 3". */
export const CHART_SEMANTIC = {
  positive: '#2E9E62',
  negative: '#C4574A',
  neutral: '#A8A29A',
  /** Own brand against competitors: ink, never indigo (reserved for links, focus and selection). */
  highlight: '#121212',
  /** For "everyone else" bars behind a highlighted own-brand bar. */
  muted: '#E3DFD9',
} as const;

/**
 * Single-hue sequential ramps, ordered strongest first. Donuts and
 * share-of-total charts use a ramp, never {@link CHART_SERIES} — parts of one
 * whole are one hue at varying strength.
 */
export const RAMP_INK = ['#121212', '#3D3B38', '#6D6963', '#A8A29A', '#D8D1C8', '#ECEAE6'] as const;
export const RAMP_INDIGO = ['#3B30B0', '#4C3FD5', '#7368E0', '#9A92EA', '#C9C4F3', '#E6E3FA'] as const;
export const RAMP_AMBER = ['#B07A12', '#D99A1C', '#E8AC2B', '#F5C556', '#F9D687', '#FCE7B8'] as const;

/** Colour for series `i`, wrapping around rather than running out. */
export function seriesColor(i: number): string {
  return CHART_SERIES[((i % CHART_SERIES.length) + CHART_SERIES.length) % CHART_SERIES.length];
}

/** Step `i` of a ramp, clamped to the darkest/lightest end instead of wrapping. */
export function rampColor(ramp: readonly string[], i: number): string {
  return ramp[Math.min(Math.max(i, 0), ramp.length - 1)];
}

/**
 * Stable colour for an arbitrary key (a competitor brand, a spend category).
 *
 * Hashes into {@link CHART_SERIES} rather than into raw HSL, so an unknown key
 * still lands on a colour that was chosen for contrast against the others.
 */
export function colorForKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return seriesColor(hash);
}

/** Axis props: no axis line, no tick line, small grey ticks. Spread onto XAxis/YAxis. */
export const CHART_AXIS = {
  tick: { fontSize: 11, fill: '#9A958E' },
  axisLine: false,
  tickLine: false,
} as const;

/** Horizontal-only grid. A vertical grid adds noise without adding readings. */
export const CHART_GRID = {
  stroke: '#EEECE8',
  strokeDasharray: '3 3',
  vertical: false,
} as const;

/** Tooltip surface. Spread onto Tooltip's `contentStyle`. */
export const TOOLTIP_STYLE = {
  backgroundColor: '#ffffff',
  border: '1px solid #E9E6E1',
  borderRadius: '10px',
  fontSize: '12px',
  boxShadow: '0 4px 12px -2px rgb(18 18 18 / 0.06)',
} as const;

/** Spread onto a recharts `<Tooltip {...CHART_TOOLTIP} />`. */
export const CHART_TOOLTIP = {
  contentStyle: TOOLTIP_STYLE,
  labelStyle: { color: '#5C5955', fontSize: 11, marginBottom: 2 },
  itemStyle: { fontSize: 12, padding: 0 },
  // recharts defaults to " : ", which reads "Money in : €".
  separator: ': ',
} as const;

/** Legend props matching the product's small, quiet legend. */
export const CHART_LEGEND = {
  wrapperStyle: { fontSize: 12, paddingTop: 8 },
  iconType: 'circle',
  iconSize: 7,
} as const;

/** Bars are rounded on the value end only. Horizontal charts grow rightward, vertical ones upward. */
export const BAR_RADIUS_HORIZONTAL = [0, 4, 4, 0] as const;
export const BAR_RADIUS_VERTICAL = [4, 4, 0, 0] as const;

/** Shared chart heights, so panels line up across dashboards. */
export const CHART_HEIGHT = { sparkline: 24, compact: 140, small: 180, default: 240, tall: 300 } as const;

/** Why the palette is shaped this way, so nobody "fixes" it by eye. */
export const CHART_PALETTE_NOTES =
  'Brand series (ink, indigo, stone first); green/red only for meaning via CHART_SEMANTIC; ramps for parts of a whole.' as const;
